import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, CheckCircle, MapPin, Phone, Mail, Clock } from 'lucide-react';
import SeoHead from '@/components/SeoHead';

const Contact = () => {
    const { toast } = useToast();
    const [formData, setFormData] = useState({
        nombre: '',
        email: '',
        telefono: '',
        mensaje: '',
        captcha: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // Simple Math Captcha
    const [captchaNum1] = useState(Math.floor(Math.random() * 10) + 1);
    const [captchaNum2] = useState(Math.floor(Math.random() * 10) + 1);

    const validate = () => {
        const newErrors = {};

        // Name: Min 3 chars, letters and spaces only
        if (!/^[a-zA-Z\s]{3,}$/.test(formData.nombre)) {
            newErrors.nombre = "El nombre debe tener al menos 3 caracteres y solo letras.";
        }

        // Email: RFC 5322 regex (simplified but robust)
        if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
            newErrors.email = "Ingrese un correo electrónico válido.";
        }

        // Phone: XXXX-XXXX
        if (!/^\d{4}-\d{4}$/.test(formData.telefono)) {
            newErrors.telefono = "El teléfono debe tener el formato XXXX-XXXX.";
        }

        // Message: 10-500 chars
        if (formData.mensaje.length < 10 || formData.mensaje.length > 500) {
            newErrors.mensaje = "El mensaje debe tener entre 10 y 500 caracteres.";
        }

        // Captcha
        if (parseInt(formData.captcha) !== captchaNum1 + captchaNum2) {
            newErrors.captcha = "La suma es incorrecta.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Auto-format phone
        if (name === 'telefono') {
            const numbers = value.replace(/\D/g, '');
            let formatted = numbers;
            if (numbers.length > 4) {
                formatted = `${numbers.slice(0, 4)}-${numbers.slice(4, 8)}`;
            }
            setFormData(prev => ({ ...prev, [name]: formatted }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast({
                title: "Error en el formulario",
                description: "Por favor corrija los errores marcados en rojo.",
                variant: "destructive"
            });
            return;
        }

        setIsSubmitting(true);

        // Simulate API call
        setTimeout(() => {
            setIsSubmitting(false);
            setIsSuccess(true);
            toast({
                title: "Mensaje enviado",
                description: "Nos pondremos en contacto contigo pronto.",
                className: "bg-green-500 text-white"
            });
        }, 2000);
    };

    const resetForm = () => {
        setFormData({
            nombre: '',
            email: '',
            telefono: '',
            mensaje: '',
            captcha: ''
        });
        setIsSuccess(false);
        setErrors({});
    };

    return (
        <div className="pt-24 pb-16 bg-gray-50 min-h-screen">
            <SeoHead
                title="Contacto | Servicios Futura"
                description="Ponte en contacto con Servicios Futura. Estamos aquí para ayudarte a encontrar tu propiedad ideal en El Salvador."
            />
            <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-primary mb-4">Contáctanos</h1>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                        Estamos aquí para ayudarte a encontrar la propiedad de tus sueños.
                        Completa el formulario y un asesor se pondrá en contacto contigo en menos de 24 horas.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                    {/* Contact Info */}
                    <div className="space-y-8">
                        <Card className="bg-primary text-white border-none shadow-xl">
                            <CardContent className="p-8 space-y-6">
                                <h3 className="text-2xl font-bold mb-6">Información de Contacto</h3>

                                <div className="flex items-start space-x-4">
                                    <MapPin className="w-6 h-6 text-secondary mt-1" />
                                    <div>
                                        <h4 className="font-semibold text-lg">Dirección Principal</h4>
                                        <p className="text-gray-300">Av. Independencia Sur, Santa Ana, El Salvador</p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-4">
                                    <Phone className="w-6 h-6 text-secondary mt-1" />
                                    <div>
                                        <h4 className="font-semibold text-lg">Teléfonos</h4>
                                        <p className="text-gray-300">+503 2525-1234 (Oficina)</p>
                                        <p className="text-gray-300">+503 7654-3210 (WhatsApp)</p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-4">
                                    <Mail className="w-6 h-6 text-secondary mt-1" />
                                    <div>
                                        <h4 className="font-semibold text-lg">Correo Electrónico</h4>
                                        <p className="text-gray-300">info@serviciosfutura.com</p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-4">
                                    <Clock className="w-6 h-6 text-secondary mt-1" />
                                    <div>
                                        <h4 className="font-semibold text-lg">Horarios de Atención</h4>
                                        <p className="text-gray-300">Lunes - Viernes: 8:00 AM - 6:00 PM</p>
                                        <p className="text-gray-300">Sábado: 8:00 AM - 12:00 PM</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <div className="h-64 rounded-xl overflow-hidden shadow-lg">
                            <iframe
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15480.256789012345!2d-89.559!3d13.994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8f62e8a31c1c1c1c%3A0x1c1c1c1c1c1c1c1c!2sSanta%20Ana%2C%20El%20Salvador!5e0!3m2!1sen!2ssv!4v1635789000000!5m2!1sen!2ssv"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                            ></iframe>
                        </div>
                    </div>

                    {/* Form */}
                    <Card className="shadow-xl">
                        <CardHeader>
                            <CardTitle>Envíanos un Mensaje</CardTitle>
                            <CardDescription>Completa todos los campos requeridos.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {isSuccess ? (
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <CheckCircle className="w-8 h-8 text-green-600" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Mensaje Enviado!</h3>
                                    <p className="text-gray-600 mb-6">Gracias por contactarnos. Un asesor revisará tu consulta y te responderá a la brevedad posible.</p>
                                    <Button onClick={resetForm} className="bg-secondary hover:bg-secondary/90">
                                        Enviar nueva consulta
                                    </Button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="space-y-2">
                                        <Label htmlFor="nombre">Nombre Completo <span className="text-red-500">*</span></Label>
                                        <Input
                                            id="nombre"
                                            name="nombre"
                                            value={formData.nombre}
                                            onChange={handleChange}
                                            placeholder="Ej. Juan Pérez"
                                            className={errors.nombre ? "border-red-500" : ""}
                                            required
                                        />
                                        {errors.nombre && <p className="text-xs text-red-500">{errors.nombre}</p>}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="email">Correo Electrónico <span className="text-red-500">*</span></Label>
                                            <Input
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="juan@ejemplo.com"
                                                className={errors.email ? "border-red-500" : ""}
                                                required
                                            />
                                            {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="telefono">Teléfono <span className="text-red-500">*</span></Label>
                                            <Input
                                                id="telefono"
                                                name="telefono"
                                                value={formData.telefono}
                                                onChange={handleChange}
                                                placeholder="XXXX-XXXX"
                                                maxLength={9}
                                                className={errors.telefono ? "border-red-500" : ""}
                                                required
                                            />
                                            {errors.telefono && <p className="text-xs text-red-500">{errors.telefono}</p>}
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="mensaje">Mensaje <span className="text-red-500">*</span></Label>
                                        <Textarea
                                            id="mensaje"
                                            name="mensaje"
                                            value={formData.mensaje}
                                            onChange={handleChange}
                                            placeholder="Estoy interesado en..."
                                            className={`h-32 ${errors.mensaje ? "border-red-500" : ""}`}
                                            required
                                        />
                                        <div className="flex justify-between text-xs text-gray-500">
                                            <span>{formData.mensaje.length}/500 caracteres</span>
                                            {errors.mensaje && <span className="text-red-500">{errors.mensaje}</span>}
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="captcha">Verificación de seguridad: ¿Cuánto es {captchaNum1} + {captchaNum2}?</Label>
                                        <Input
                                            id="captcha"
                                            name="captcha"
                                            value={formData.captcha}
                                            onChange={handleChange}
                                            placeholder="Respuesta"
                                            className={`w-32 ${errors.captcha ? "border-red-500" : ""}`}
                                        />
                                        {errors.captcha && <p className="text-xs text-red-500">{errors.captcha}</p>}
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full bg-primary hover:bg-primary/90 text-white h-12 text-lg"
                                        disabled={isSubmitting || !formData.nombre || !formData.email || !formData.telefono || !formData.mensaje}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando...
                                            </>
                                        ) : (
                                            "Enviar Mensaje"
                                        )}
                                    </Button>
                                </form>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Contact;
